import {
  Accordion as AccordionBase,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/common/components/ui/accordion";
import { cn } from "@/common/utils/cn";
import { createRef } from "react";
import { twMerge } from "tailwind-merge";

export interface AccordionsProps {
  items?: {
    index: number;
    id?: string;
    head?: JSX.Element | string;
    body?: JSX.Element | string;
    hide?: boolean;
    disabled?: boolean;
    dataTestid?: string;
  }[];
  accordionsClassName?: string;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  onToggle?: (isOpen: boolean) => void;
  defaultFirstItemOpened?: boolean;
}

const Accordions = ({
  items,
  accordionsClassName,
  className,
  triggerClassName,
  contentClassName,
  onToggle,
  defaultFirstItemOpened,
}: AccordionsProps) => {
  const contentRef = createRef<HTMLDivElement>();

  const defaultOnToggle = (isOpen: boolean) => {
    if (isOpen) {
      (contentRef.current as HTMLDivElement)?.classList?.remove("hidden");
      setTimeout(() => (contentRef.current as HTMLDivElement)?.classList?.add("overflow-visible"), 300);
    } else {
      setTimeout(() => (contentRef.current as HTMLDivElement)?.classList?.add("hidden"), 100);
      (contentRef.current as HTMLDivElement)?.classList?.remove("overflow-visible");
    }
  };

  const handleOnToggle = (itemName: string) => {
    defaultOnToggle(!!itemName);

    onToggle && onToggle(!!itemName);
  };

  return (
    <AccordionBase
      type="single"
      collapsible
      onValueChange={handleOnToggle}
      defaultValue={defaultFirstItemOpened ? items?.[0]?.id : undefined}
      className={cn("flex flex-col gap-4", accordionsClassName)}
    >
      {items?.map(({ index, id, head, body, hide, disabled, dataTestid }) =>
        hide ? null : disabled ? (
          <div
            key={id || `accordion-${index}`}
            className={twMerge('w-full p-4 rounded-sm bg-white-100', className)}
          >
            <div className="flex min-h-14 items-center py-4">
              {head}
            </div>
          </div>
        ) : (
          <AccordionItem className={twMerge("w-full p-4 rounded-sm bg-white-100", className)} value={id || `accordion-${index}`} key={id || `accordion-${index}`}>
            <AccordionTrigger
              className={twMerge("py-4", triggerClassName)}
              disabled={disabled}
              iconStyles={disabled ? 'hidden' : ''}
              data-testid={dataTestid}
            >
              {head}
            </AccordionTrigger>
          <AccordionContent ref={contentRef} containerClassName={contentClassName} >
              {body}
            </AccordionContent>
          </AccordionItem>
        ))}
    </AccordionBase>
  );
};

export default Accordions;
